import { NavItemType } from 'types/menu';
import { useHasAuthorization } from 'api/authUser';
import { AuthUserPermissionItem } from 'types/auth';

// Permission bits (bitmask)
export const PERMISSION_VIEW = 1;
export const PERMISSION_ADD = 2;
export const PERMISSION_EDIT = 4;
export const PERMISSION_DELETE = 8;
export const PERMISSION_PRINT = 16;

function getFeaturePermissionsValue(featureID: number | undefined, userPerms: AuthUserPermissionItem[] | undefined): number | null {
  // If no featureID, item has no permission binding (return null to allow)
  if (!featureID) return null;

  // If permissions not loaded or empty, deny access (return 0)
  if (!userPerms || userPerms.length === 0) return 0;

  // Find the feature in user permissions
  const feature = userPerms.find((p) => p.featureID === featureID);

  // If feature not found in user permissions, deny access (return 0)
  if (!feature) return 0;

  // Return the permission value
  return feature.permissions ?? 0;
}

export function hasPermission(featureID: number | undefined, userPerms: AuthUserPermissionItem[] | undefined, bit: number) {
  const value = getFeaturePermissionsValue(featureID, userPerms);

  // If no feature binding (null), allow access
  if (value === null) return true;

  // Check if the specific permission bit is set
  return (value & bit) > 0;
}

export function hasReadPermission(featureID: number | undefined, userPerms: AuthUserPermissionItem[] | undefined) {
  return hasPermission(featureID, userPerms, PERMISSION_VIEW);
}

export function hasAddPermission(featureID: number | undefined, userPerms: AuthUserPermissionItem[] | undefined) {
  return hasPermission(featureID, userPerms, PERMISSION_ADD);
}

export function hasEditPermission(featureID: number | undefined, userPerms: AuthUserPermissionItem[] | undefined) {
  return hasPermission(featureID, userPerms, PERMISSION_EDIT);
}

export function hasDeletePermission(featureID: number | undefined, userPerms: AuthUserPermissionItem[] | undefined) {
  return hasPermission(featureID, userPerms, PERMISSION_DELETE);
}

export function hasPrintPermission(featureID: number | undefined, userPerms: AuthUserPermissionItem[] | undefined) {
  return hasPermission(featureID, userPerms, PERMISSION_PRINT);
}

export function getFeaturePermissions(featureID: number | undefined, userPerms: AuthUserPermissionItem[] | undefined) {
  return {
    canView: hasReadPermission(featureID, userPerms),
    canAdd: hasAddPermission(featureID, userPerms),
    canEdit: hasEditPermission(featureID, userPerms),
    canDelete: hasDeletePermission(featureID, userPerms),
    canPrint: hasPrintPermission(featureID, userPerms)
  };
}

export function filterMenuByPermissions(menu: { items: NavItemType[] }, userPerms: AuthUserPermissionItem[] | undefined) {
  const filterNode = (node: NavItemType): NavItemType | null => {
    // If node has featureID, check read permission
    if (typeof node.featureID === 'number') {
      if (!hasReadPermission(node.featureID, userPerms)) {
        return null;
      }
    }

    // Recurse into children/elements
    const next: NavItemType = { ...node };
    if (Array.isArray(node.children)) {
      const filteredChildren = node.children
        .map(filterNode)
        .filter((n): n is NavItemType => n !== null);
      next.children = filteredChildren;

      // Hide groups that have no visible children after filtering (unless it's a direct item with a URL)
      if (node.type === 'group' && filteredChildren.length === 0) {
        return null;
      }
    }
    if (Array.isArray(node.elements)) {
      const filteredElements = node.elements
        .map(filterNode)
        .filter((n): n is NavItemType => n !== null);
      next.elements = filteredElements;

      // Hide items that have no visible elements after filtering
      if (filteredElements.length === 0 && node.elements.length > 0) {
        return null;
      }
    }

    return next;
  };

  const filteredItems = (menu.items || [])
    .map(filterNode)
    .filter((n): n is NavItemType => n !== null);

  return { items: filteredItems } as { items: NavItemType[] };
}


// ==============================|| RUNTIME PERMISSION HELPERS (API-BASED) ||============================== //

/**
 * Check permission in real-time via API and optionally redirect to no-permission.
 * Returns true if allowed, false if denied (and redirects when navigate provided).
 */
export async function ensurePermission(
  featureID: number | null | undefined,
  permissionBit: number,
  navigate?: (path: string, opts?: { replace?: boolean }) => void
): Promise<boolean> {
  if (typeof featureID !== 'number') return true;
  try {
    const allowed = await useHasAuthorization(featureID, permissionBit);
    if (allowed === false) {
      if (navigate) navigate('/no-permission', { replace: true });
      return false;
    }
    return true;
  } catch {
    if (navigate) navigate('/no-permission', { replace: true });
    return false;
  }
}

export function ensureCanView(featureID: number | null | undefined, navigate?: (path: string, opts?: { replace?: boolean }) => void) {
  return ensurePermission(featureID, PERMISSION_VIEW, navigate);
}

export function ensureCanAdd(featureID: number | null | undefined, navigate?: (path: string, opts?: { replace?: boolean }) => void) {
  return ensurePermission(featureID, PERMISSION_ADD, navigate);
}

export function ensureCanEdit(featureID: number | null | undefined, navigate?: (path: string, opts?: { replace?: boolean }) => void) {
  return ensurePermission(featureID, PERMISSION_EDIT, navigate);
}

export function ensureCanDelete(featureID: number | null | undefined, navigate?: (path: string, opts?: { replace?: boolean }) => void) {
  return ensurePermission(featureID, PERMISSION_DELETE, navigate);
}

export function ensureCanPrint(featureID: number | null | undefined, navigate?: (path: string, opts?: { replace?: boolean }) => void) {
  return ensurePermission(featureID, PERMISSION_PRINT, navigate);
}


